import { useCallback, useEffect, useMemo, useState } from "react";
import { IFRAME_ID } from "@circle-react-shared/BuilderUI/Frames/constants";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { useIFrameDocument } from "@circle-react-shared/BuilderUI/Hooks/useIFrameDocument";

export interface Positions {
  absoluteBottom: number;
  absoluteLeft: number;
  absoluteRight: number;
  absoluteTop: number;
  elementRect: DOMRect;
  iframeRect: DOMRect;
}

export interface UseCanvasToolsPositionsReturn {
  iFrameDocument: Document | null;
  isFirstBlock: boolean;
  positions: Positions | null;
  selectedBlock: { id: string } | null;
  updatePosition: () => void;
}

export const useCanvasToolsPositions = (): UseCanvasToolsPositionsReturn => {
  const selectedBlockPath = useBuilderStore(state => state.selectedBlockPath);
  const selectedBlock = useBuilderStore(state =>
    state.getSelectedBlock(),
  )?.block;
  const iFrameDocument = useIFrameDocument(IFRAME_ID, selectedBlockPath ?? "");

  const [positions, setElementRect] = useState<Positions | null>(null);

  const getElementRect = useCallback(() => {
    if (!selectedBlock?.id || !iFrameDocument) {
      return null;
    }

    const element = iFrameDocument.getElementById(selectedBlock.id);
    const iframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement;

    if (!element || !iframe) {
      return null;
    }

    const elementRect = element.getBoundingClientRect();
    const iframeRect = iframe.getBoundingClientRect();

    return {
      elementRect,
      iframeRect,
      absoluteTop: iframeRect.top + elementRect.top,
      absoluteLeft: iframeRect.left + elementRect.left,
      absoluteRight: iframeRect.left + elementRect.right,
      absoluteBottom: iframeRect.top + elementRect.bottom,
    };
  }, [selectedBlock, iFrameDocument]);

  const updatePosition = useCallback(() => {
    const rect = getElementRect();
    setElementRect(rect);
  }, [getElementRect]);

  const resizeObserver = useMemo(
    () => new ResizeObserver(() => updatePosition()),
    [updatePosition],
  );

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    if (!iFrameDocument) {
      return;
    }

    const handlePositionUpdate = () => updatePosition();

    iFrameDocument.addEventListener("scroll", handlePositionUpdate);
    window.addEventListener("resize", handlePositionUpdate);
    window.addEventListener("scroll", handlePositionUpdate);

    const iframe = document.getElementById(IFRAME_ID);
    if (iframe) {
      resizeObserver.observe(iframe);
    }

    if (selectedBlock?.id) {
      const blockElement = iFrameDocument.getElementById(selectedBlock.id);
      if (blockElement) {
        resizeObserver.observe(blockElement);
      }
    }

    return () => {
      iFrameDocument.removeEventListener("scroll", handlePositionUpdate);
      window.removeEventListener("resize", handlePositionUpdate);
      window.removeEventListener("scroll", handlePositionUpdate);
      resizeObserver.disconnect();
    };
  }, [iFrameDocument, selectedBlock, updatePosition, resizeObserver]);

  const isFirstBlock = useMemo(
    () => selectedBlockPath === "blocks.props.children.value.0",
    [selectedBlockPath],
  );

  return {
    positions,
    iFrameDocument,
    selectedBlock: selectedBlock || null,
    updatePosition,
    isFirstBlock,
  };
};
